import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import * as yup from 'yup'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import ReactQuill from 'react-quill'
import { decode, encode } from 'html-entities'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Dialog from 'components/dialog/dialog'
// import type {
//   PartialVoucherCreateDataProps,
//   PartialVoucherCreateDataRequestProps,
//   PartialVoucherCreateProps
// } from 'modules/partial'
import type { CallbackHandler, DetailStateProps, ErrorProps } from 'modules/types'
import FORMIK from 'modules/formik'
import {
  useGetVoucherCategoryDropdownMutation,
  useGetVoucherSubCategoryDropdownMutation
} from 'store/voucherCategory'
import GlobalStyle from 'modules/styles'
import { useGetTokenPartnerDropdownMutation } from 'store/token'
import { useGetTokenCategoryDropdownMutation } from 'store/tokenRequest'
import GLOBAL from 'modules/global'
import { useCreateListVoucherMutation, useGetListVoucherDetailQuery } from 'store/voucher'
import { useGetCarModelDropdownListMutation } from 'store/dropdown'
import { useGetCitiesDropdownMutation, useGetProvinciesDropdownMutation } from 'store/region'
import Notification from 'components/notification'
import Loading from 'components/loading/loading'
import BackdropImage from 'components/backdropImage/backdropImage'
import DialogStyle from './style'

const Duplicate = ({ open, id, onClose }: DetailStateProps & { id: string | number }) => {
  if (!open) return <></>

  const { data, isFetching, isLoading } = useGetListVoucherDetailQuery(id)
  const loading = !data && (isLoading || isFetching)

  const [getVoucherCategoryDropdown, dropdownCategory] = useGetVoucherCategoryDropdownMutation()
  const [getVoucherSubCategoryDropdown, dropdownSubCategory] =
    useGetVoucherSubCategoryDropdownMutation()
  const [getTokenPartnerDropdown, partnerDropdown] = useGetTokenPartnerDropdownMutation()
  const [getTokenCategoryDropdown, tokenCategoryDropdown] = useGetTokenCategoryDropdownMutation()
  const [getCarModelDropdownList, carModelList] = useGetCarModelDropdownListMutation()
  const [getProvinceDropdown, provinceDropdown] = useGetProvinciesDropdownMutation()
  const [getCityDropdown, cityDropdown] = useGetCitiesDropdownMutation()
  const [createVoucher, create] = useCreateListVoucherMutation()

  const [prevRegistrationDateFrom, setPrevRegistrationDateFrom] = useState<string>('');
  const [prevRegistrationDateTo, setPrevRegistrationDateTo] = useState<string>('');
  const [DoneChangesDateToValue, setDoneChangesDateTo] = useState<boolean>(false);
  const [isMaxDateDisabled, setIsMaxDateDisabled] = useState<boolean>(true);

  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>(null)
  const [previewBanner, setPreviewBanner] = useState<string | ArrayBuffer | null>(null)
  const [previewBackdrop, setPreviewBackdrop] = useState<boolean>(false)
  const [bannerBackdrop, setBannerBackdrop] = useState<boolean>(false)
  const [carModel, setCarModel] = useState<CarModelDropDown[]>([])
  const [userTypeProvince, setUserTypeProvince] = useState<LocationDropDown[]>([])
  const [userTypeCity, setUserTypeCity] = useState<LocationDropDown[]>([])
  const [messageUntil, setMessageUntil] = useState<string>('')
  const [getMinimum, setMinimum] = useState<string>('')
  const [getMaximum, setMaximum] = useState<string>('')
  const [isClearDisabled, setIsClearDisabled] = useState<boolean>(true);

  const scheme = yup.object<PartialVoucherCreateProps>({
    image: yup.mixed().required('Voucher Image is required'),
    banner: yup.mixed().required('Voucher Image Banner is required'),
    data: yup.object<PartialVoucherCreateDataProps>({
      nameEn: yup.string().required('Name in English is required'),
      nameId: yup.string().required('Name in Indonesia is required'),
      voucherType: yup.string().required('Voucher Type is required'),
      bookingType: yup.string().when('voucherType', {
        is: (val: string) => val === 'Booking',
        then: (schema) => schema.required('Booking Type is required')
      }),
      voucherCategoryId: yup.number().required('Voucher Category ID is required'),
      voucherSubCategoryId: yup.number().required('Voucher Sub Category ID is required'),
      voucherTokenCategoryId: yup.number().required('Voucher Token Category ID is required'),
      partnerId: yup.number().required('Partner ID is required'),
      applicableAreaMode: yup.string().required('Applicable Area Mode is required'),
      termsAndConditionsEn: yup.string().required('Terms and Condition in English is required'),
      termsAndConditionsId: yup.string().required('Terms and Condition in Indonesia is required'),
      informationEn: yup.string().required('Information in English is required'),
      informationId: yup.string().required('Information in Indonesia is required'),
      generateType: yup.string().required('Token Source is required'),
      description: yup.string(),
      payloadGenerateRequest: yup
        .object<PartialVoucherCreateDataRequestProps>()
        .when('generateType', {
          is: (value: string) => value === 'Request',
          then: () =>
            yup.object({
              requestName: yup.string().required('Request Name is required'),
              quantity: yup.string().required('Quantity is required'),
              validFrom: yup.string().required('Valid From is required'),
              validUntil: yup.string().required('Valid Until is required')
            })
        }),
      carModels: yup.array().of(yup.object().shape({
        id: yup.number().required(),
        name: yup.string().required(),
      })).nullable(),
      locations: yup.array().of(yup.object().shape({
        cityId: yup.number().required(),
        cityName: yup.string().notRequired(),
        provinceId: yup.number().required(),
        provinceName: yup.string().notRequired(),
      })).nullable(),
      registrationDateFrom: yup.string().nullable(),
      registrationDateTo: yup.string().nullable(),
    }),
    importToken: yup.mixed()
  })

  const initialValues: PartialVoucherCreateProps = {
    image: {} as File,
    banner: {} as File,
    importToken: {} as File,
    data: {
      nameEn: (data && data.nameEn) || '',
      nameId: (data && data.nameId) || '',
      voucherType: (data && data.voucherType) || '',
      bookingType: (data && data.bookingType) || '',
      voucherCategoryId: (data && data.voucherCategoryId) || null,
      voucherSubCategoryId: (data && data.voucherSubCategoryId) || null,
      voucherTokenCategoryId: (data && data.tokenCategoryId) || null,
      partnerId: (data && data.partnerId) || null,
      applicableAreaMode: (data && data.applicableAreaMode) || '',
      termsAndConditionsEn: (data && decode(data.termsAndConditionsEn)) || '',
      termsAndConditionsId: (data && decode(data.termsAndConditionsId)) || '',
      informationEn: (data && decode(data.descriptionEn)) || '',
      informationId: (data && decode(data.descriptionId)) || '',
      generateType: (data && data.generateType) || 'None',
      visibility: data && data.visibility,
      description: '',
      payloadGenerateRequest: {
        requestName: '',
        quantity: '',
        validFrom: '',
        validUntil: ''
      },
      carModels: (data && data.carModels && data.carModels.length > 0) ? data.carModels.map((item) => ({
        id: item.id,
        name: item.name
      })) : [],
      locations: (data && data.locations && data.locations.length > 0) ? data.locations.map((item) => ({
        cityId: item.cityId,
        cityName: item.cityName,
        provinceId: item.provinceId,
        provinceName: item.provinceName,
      })) : [],
      registrationDateFrom: (data && GLOBAL.formatDateddMMYYLongDateDetail(data.registrationDateFrom)) || '',
      registrationDateTo: (data && GLOBAL.formatDateddMMYYLongDateDetail(data.registrationDateTo)) || ''
      // registrationDateFrom: formatDateWithTime(data?.registrationDateFrom, "00:00:00"),
      // registrationDateTo: formatDateWithTime(data?.registrationDateTo, "23:59:59"),
    }
  }

  const formatDateWithTime = (dateStr: string | undefined | null, time: string) => {
    if (!dateStr) return ""
    const datePart = dayjs(dateStr).format('YYYY-MM-DD')
    return `${datePart} ${time}`
  }

  const formik = FORMIK.useFormFormik<PartialVoucherCreateProps>(
    scheme,
    initialValues,
    (values) => {
      const formData = new FormData()
      const carModels = carModel.map(carModel => carModel.id);
      const updated = {
        ...values.data,
        generateType: values.data.generateType === 'None' ? null : values.data.generateType,
        termsAndConditionsEn: encode(values.data.termsAndConditionsEn),
        termsAndConditionsId: encode(values.data.termsAndConditionsId),
        informationEn: encode(values.data.informationEn),
        informationId: encode(values.data.informationId),
        imageUrl: data && data.image,
        bannerUrl: data && data.banner,
        carModels: carModels,
        // registrationDateFrom: (values.data.registrationDateFrom && values.data.registrationDateFrom !== "-") ? values.data.registrationDateFrom : "",
        // registrationDateTo: (values.data.registrationDateTo && values.data.registrationDateTo !== "-") ? values.data.registrationDateTo : "",
        // registrationDateFrom: formatDateWithTime(values.data.registrationDateFrom, "00:00:00"),
        // registrationDateTo: formatDateWithTime(values.data.registrationDateTo, "23:59:59"),
        registrationDateFrom: values.data.registrationDateFrom === "-" ? "" : formatDateWithTime(values.data.registrationDateFrom, "00:00:00"),
        registrationDateTo: values.data.registrationDateTo === "-" ? "" : formatDateWithTime(values.data.registrationDateTo, "23:59:59"),
      }

      if (isEmpty(values.image) && data) formData.append('image', data.image || '')
      else formData.append('image', values.image)

      if (isEmpty(values.banner) && data) formData.append('banner', data.banner || '')
      else formData.append('banner', values.banner)

      if (values.importToken) formData.append('importToken', values.importToken)

      formData.append('data', JSON.stringify(updated))

      createVoucher(formData)
    }
  )

  const fileOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string,
    callback: CallbackHandler<string | ArrayBuffer | null>
  ) => {
    const files = e && e.target && e.target.files
    const notEmpty = files && files.length > 0 && !isEmpty(files)

    if (notEmpty) {
      const reader = new FileReader()

      reader.onload = () => callback(reader.result)
      reader.readAsDataURL(files[0])

      formik.setFieldValue(field, files[0])
      return
    }

    formik.setFieldValue(field, {} as File)
    return callback(null)
  }

  const isEndDateGreaterThanStartDate = () => {
    if (
      messageUntil && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))
    ) {
      return true
    } else {
      return false
    }
  }

  const error = formik.errors && (formik.errors.data as unknown as PartialVoucherCreateDataProps)
  const touched = formik.touched && (formik.touched as PartialVoucherCreateDataProps)
  const requestToken = formik.values.data.payloadGenerateRequest

  const checkVechileRegistrationDateMinimum = () => {
    if (getMinimum && !getMaximum) {
      return true
    } else if (!getMinimum && getMaximum) {
      return true
    } else {
      return false
    }
  }

  const handleRegistrationDateFromChange = (newDate: Dayjs | null) => {
    const newDateValue = (!isEmpty(newDate) && newDate?.format('YYYY-MM-DD HH:mm:ss')) || '';
    formik.setFieldValue('data.registrationDateFrom', newDateValue);
    setMinimum(newDateValue);
    if (prevRegistrationDateFrom !== newDateValue) {
      formik.setFieldValue('data.registrationDateTo', '');
      setPrevRegistrationDateTo('');
      setDoneChangesDateTo(true);
      setIsMaxDateDisabled(false);
    }
    setPrevRegistrationDateFrom(newDateValue);
  };

  const handleRegistrationDateToChange = (newDate: Dayjs | null) => {
    const newDateValue = (!isEmpty(newDate) && newDate?.format('YYYY-MM-DD HH:mm:ss')) || '';
    formik.setFieldValue('data.registrationDateTo', newDateValue);
    setMaximum(newDateValue);
    if (prevRegistrationDateTo !== newDateValue) {
      setDoneChangesDateTo(false);
      // formik.setFieldValue('data.registrationDateFrom', '');
      // setMinimum(''); // Reset minimum value
    }
    setPrevRegistrationDateTo(newDateValue);
  };

  const disabled =
    formik.values.data.generateType === 'Import'
      ? !formik.isValid ||
      isEmpty(previewImage) ||
      isEmpty(previewBanner) ||
      !('size' in formik.values.importToken)
      : !formik.isValid || isEmpty(previewImage) || isEmpty(previewBanner) ||
      isEndDateGreaterThanStartDate() || (userTypeProvince.length > 0 && userTypeCity.length === 0) ||
      checkVechileRegistrationDateMinimum() ||
      (prevRegistrationDateFrom && prevRegistrationDateTo && dayjs(prevRegistrationDateFrom).isAfter(dayjs(prevRegistrationDateTo))) ||
      false || DoneChangesDateToValue;;

  useEffect(() => {
    if (data) {
      if (data.voucherCategoryId) getVoucherCategoryDropdown()
      if (data.voucherSubCategoryId) getVoucherSubCategoryDropdown({ id: data.voucherCategoryId })
      if (data.partnerId) getTokenPartnerDropdown()
      if (data.tokenCategoryId) getTokenCategoryDropdown()
      if (data.image) setPreviewImage(data.image)
      if (data.banner) setPreviewBanner(data.banner)
      if (data.carModels) {
        getCarModelDropdownList()
        const defaultCarModel =
          (data &&
            data.carModels &&
            data.carModels.map((item) => ({
              id: item.id,
              name: item.name
            }))) ||
          []
        setCarModel(defaultCarModel)
      }
      if (data.locations) {
        // useGetProvinciesDropdownMutation()
        const defaultProvince =
          (data &&
            data.locations &&
            data.locations.map((item) => ({
              cityId: 0,
              cityName: '',
              provinceId: item.provinceId,
              provinceName: item.provinceName
            }))) ||
          []
        setUserTypeProvince(defaultProvince)

        // useGetCitiesDropdownMutation()
        const defaultCity =
          (data &&
            data.locations &&
            data.locations.map((item) => ({
              cityId: item.cityId,
              cityName: item.cityName,
              provinceId: item.provinceId,
              provinceName: item.provinceName
            }))) ||
          []
        setUserTypeCity(defaultCity)
      }
    }
  }, [data])

  useEffect(() => {
    setIsClearDisabled(disabled);
  }, [disabled]);

  return (
    <>
      <Dialog
        title='Duplicate Voucher'
        open={open}
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={create.isLoading}
        isDisabled={disabled}
        maxWidth='md'
      >
        <Container {...DialogStyle.Container}>
          {loading && <Loading />}
          {!loading && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                <TextField
                  id='nameEn'
                  variant='outlined'
                  label='Voucher EN-US Name *'
                  value={formik.values.data.nameEn}
                  onChange={(event) => formik.setFieldValue('data.nameEn', event.target.value)}
                  onBlur={formik.handleBlur}
                  error={touched && Boolean(touched.nameEn) && error && Boolean(error.nameEn)}
                  helperText={touched && touched.nameEn && error && error.nameEn}
                  fullWidth
                />
                <TextField
                  id='nameId'
                  variant='outlined'
                  label='Voucher ID Name *'
                  value={formik.values.data.nameId}
                  onChange={(event) => formik.setFieldValue('data.nameId', event.target.value)}
                  onBlur={formik.handleBlur}
                  error={touched && Boolean(touched.nameId) && error && Boolean(error.nameId)}
                  helperText={touched && touched.nameId && error && error.nameId}
                  fullWidth
                />
                <Autocomplete
                  options={(dropdownCategory && dropdownCategory.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getVoucherCategoryDropdown()}
                  onChange={(_, voucherCategory) => {
                    dropdownSubCategory.reset()
                    formik.setFieldValue(
                      'data.voucherCategoryId',
                      String((voucherCategory && voucherCategory.id) || null)
                    )
                  }}
                  value={
                    (dropdownCategory &&
                      dropdownCategory.data &&
                      dropdownCategory.data.find(
                        (e) => e.id == formik.values.data.voucherCategoryId
                      )) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='voucherCategoryId'
                      label='Category ID *'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dropdownCategory.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(dropdownSubCategory && dropdownSubCategory.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() =>
                    formik.values.data.voucherCategoryId &&
                    getVoucherSubCategoryDropdown({ id: formik.values.data.voucherCategoryId })
                  }
                  onChange={(_, voucherCategory) =>
                    formik.setFieldValue(
                      'data.voucherSubCategoryId',
                      String((voucherCategory && voucherCategory.id) || null)
                    )
                  }
                  value={
                    (dropdownSubCategory &&
                      dropdownSubCategory.data &&
                      dropdownSubCategory.data.find(
                        (e) => e.id == formik.values.data.voucherSubCategoryId
                      )) ||
                    null
                  }
                  disabled={
                    formik.values.data.voucherCategoryId == null ||
                    formik.values.data.voucherCategoryId <= 0
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='voucherSubCategoryId'
                      label='Voucher Sub Category ID *'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dropdownSubCategory.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(partnerDropdown && partnerDropdown.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getTokenPartnerDropdown()}
                  onChange={(_, partner) =>
                    formik.setFieldValue('data.partnerId', String((partner && partner.id) || null))
                  }
                  value={
                    (partnerDropdown &&
                      partnerDropdown.data &&
                      partnerDropdown.data.find((e) => e.id == formik.values.data.partnerId)) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='partnerId'
                      label='Partner *'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {partnerDropdown.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(tokenCategoryDropdown && tokenCategoryDropdown.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getTokenCategoryDropdown()}
                  onChange={(_, tokenCategory) =>
                    formik.setFieldValue(
                      'data.voucherTokenCategoryId',
                      String((tokenCategory && tokenCategory.id) || null)
                    )
                  }
                  value={
                    (tokenCategoryDropdown &&
                      tokenCategoryDropdown.data &&
                      tokenCategoryDropdown.data.find(
                        (e) => e.id == formik.values.data.voucherTokenCategoryId
                      )) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='tokenCategoryId'
                      label='Token Category *'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {tokenCategoryDropdown.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Description ID *</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.informationId}
                    onChange={(e) =>
                      formik.setFieldValue('data.informationId', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Description EN-US *</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.informationEn}
                    onChange={(e) =>
                      formik.setFieldValue('data.informationEn', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Terms and Condition ID *</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.termsAndConditionsId}
                    onChange={(e) =>
                      formik.setFieldValue('data.termsAndConditionsId', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Terms and Condition EN-US *</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.termsAndConditionsEn}
                    onChange={(e) =>
                      formik.setFieldValue('data.termsAndConditionsEn', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                <FormControl fullWidth>
                  <InputLabel id='voucherType'>Voucher Type *</InputLabel>
                  <Select
                    labelId='voucherType'
                    id='voucherType'
                    variant='outlined'
                    label='Voucher Type *'
                    value={formik.values.data.voucherType}
                    onChange={(event) => {
                      formik.setFieldValue('data.bookingType', '')
                      formik.setFieldValue('data.voucherType', event.target.value)
                    }}
                    error={
                      touched && Boolean(touched.voucherType) && error && Boolean(error.voucherType)
                    }
                  >
                    <MenuItem value='Voucher'>Voucher</MenuItem>
                    <MenuItem value='Booking'>Booking</MenuItem>
                  </Select>
                  {touched && touched.voucherType && error && (
                    <FormHelperText error id='voucherType'>
                      {error.voucherType}
                    </FormHelperText>
                  )}
                </FormControl>
                {formik.values.data.voucherType == 'Booking' && (
                  <FormControl fullWidth>
                    <InputLabel id='bookingType'>Booking Type *</InputLabel>
                    <Select
                      labelId='bookingType'
                      id='bookingType'
                      value={formik.values.data.bookingType}
                      label='Booking Type *'
                      onChange={(event) =>
                        formik.setFieldValue('data.bookingType', event.target.value)
                      }
                      error={
                        touched &&
                        Boolean(touched.bookingType) &&
                        error &&
                        Boolean(error.bookingType)
                      }
                    >
                      <MenuItem value='Lounge & Airport VIP Assistance'>
                        Lounge & Airport VIP Assistance
                      </MenuItem>
                      <MenuItem value='Airport VIP Assistance'>Airport VIP Assistance</MenuItem>
                      <MenuItem value='Airport Parking'>Airport Parking</MenuItem>
                      <MenuItem value='City Lounge'>City Lounge</MenuItem>
                      <MenuItem value='Mall Parking'>Mall Parking</MenuItem>
                    </Select>
                    {touched && touched.bookingType && error && (
                      <FormHelperText error id='bookingType'>
                        {error.bookingType}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
                {/* <FormControl fullWidth>
                <InputLabel id='applicableAreaMode'>Applicable Area Mode *</InputLabel>
                <Select
                  labelId='applicableAreaMode'
                  id='applicableAreaMode'
                  variant='outlined'
                  label='Applicable Area Mode *'
                  value={formik.values.data.applicableAreaMode}
                  readOnly
                  onChange={(event) =>
                    formik.setFieldValue('data.applicableAreaMode', event.target.value)
                  }
                  error={
                    touched &&
                    Boolean(touched.applicableAreaMode) &&
                    error &&
                    Boolean(error.applicableAreaMode)
                  }
                >
                  <MenuItem value='All Branches'>All Branches</MenuItem>
                  <MenuItem value='Specific Branches'>Specific Branches</MenuItem>
                </Select>
                {touched && touched.applicableAreaMode && error && (
                  <FormHelperText error id='applicableAreaMode'>
                    {error.applicableAreaMode}
                  </FormHelperText>
                )}
              </FormControl> */}
                <FormControl fullWidth>
                  <InputLabel id='generateType'>Token Source *</InputLabel>
                  <Select
                    labelId='generateType'
                    id='generateType'
                    variant='outlined'
                    label='Token Source *'
                    value={formik.values.data.generateType}
                    onChange={(event) => {
                      formik.setFieldValue('importToken', {} as File)
                      formik.setFieldValue('data.payloadGenerateRequest.requestName', '')
                      formik.setFieldValue('data.payloadGenerateRequest.quantity', '')
                      formik.setFieldValue('data.payloadGenerateRequest.validFrom', '')
                      formik.setFieldValue('data.payloadGenerateRequest.validUntil', '')
                      formik.setFieldValue('data.generateType', event.target.value)
                    }}
                    error={
                      touched &&
                      Boolean(touched.generateType) &&
                      error &&
                      Boolean(error.generateType)
                    }
                  >
                    <MenuItem value='None'>None</MenuItem>
                    <MenuItem value='Import'>Import</MenuItem>
                    <MenuItem value='Request'>Request</MenuItem>
                  </Select>
                  {touched && touched.generateType && error && (
                    <FormHelperText error id='generateType'>
                      {error.generateType}
                    </FormHelperText>
                  )}
                </FormControl>
                {formik.values.data.generateType === 'Import' && (
                  <>
                    <Typography {...DialogStyle.Title}>Token Import</Typography>
                    <TextField
                      id='importToken'
                      label='Import Token *'
                      type='file'
                      placeholder='Import Token'
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        accept:
                          '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const files = e && e.target && e.target.files
                        const notEmpty = files && files.length > 0 && !isEmpty(files)

                        if (notEmpty) {
                          const reader = new FileReader()

                          reader.readAsDataURL(files[0])
                          formik.setFieldValue('importToken', files[0])
                          return
                        }

                        formik.setFieldValue('importToken', {} as File)
                      }}
                      onBlur={formik.handleBlur}
                      fullWidth
                    />
                  </>
                )}
                {formik.values.data.generateType === 'Request' && (
                  <>
                    <Typography {...DialogStyle.Title}>Token Request</Typography>
                    <TextField
                      id='data.payloadGenerateRequest.requestName'
                      variant='outlined'
                      label='Request Name *'
                      value={requestToken && requestToken.requestName}
                      onChange={(event) =>
                        formik.setFieldValue(
                          'data.payloadGenerateRequest.requestName',
                          event.target.value
                        )
                      }
                      onBlur={formik.handleBlur}
                      error={
                        touched &&
                        touched.payloadGenerateRequest &&
                        Boolean(touched.payloadGenerateRequest.requestName) &&
                        error &&
                        error.payloadGenerateRequest &&
                        Boolean(error.payloadGenerateRequest.requestName)
                      }
                      helperText={
                        touched &&
                        touched.payloadGenerateRequest &&
                        touched.payloadGenerateRequest.requestName &&
                        error &&
                        error &&
                        error.payloadGenerateRequest &&
                        error.payloadGenerateRequest.requestName
                      }
                      fullWidth
                    />
                    <TextField
                      id='data.payloadGenerateRequest.quantity'
                      variant='outlined'
                      label='Quantity *'
                      type='number'
                      value={
                        requestToken && requestToken.quantity && requestToken.quantity.toString()
                      }
                      onKeyDown={(event) => {
                        if (/^[.,\-\b]+$/.test(event.key)) event.preventDefault()
                      }}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      inputProps={{ autoComplete: 'off' }}
                      error={
                        touched &&
                        touched.payloadGenerateRequest &&
                        Boolean(touched.payloadGenerateRequest.quantity) &&
                        error &&
                        error.payloadGenerateRequest &&
                        Boolean(error.payloadGenerateRequest.quantity)
                      }
                      helperText={
                        touched &&
                        touched.payloadGenerateRequest &&
                        touched.payloadGenerateRequest.quantity &&
                        error &&
                        error &&
                        error.payloadGenerateRequest &&
                        error.payloadGenerateRequest.quantity
                      }
                      fullWidth
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={
                          requestToken && requestToken.validFrom
                            ? dayjs(requestToken.validFrom)
                            : undefined
                        }
                        onChange={(event: Dayjs | null) =>
                          formik.setFieldValue(
                            'data.payloadGenerateRequest.validFrom',
                            (event && event.format('YYYY-MM-DD')) || ''
                          )
                        }
                        label='Valid From *'
                        format='DD-MM-YYYY'
                      />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={
                          requestToken && requestToken.validUntil
                            ? dayjs(requestToken.validUntil)
                            : undefined
                        }
                        minDate={
                          requestToken && requestToken.validFrom
                            ? dayjs(requestToken.validFrom)
                            : undefined
                        }
                        onChange={(event: Dayjs | null) =>
                          formik.setFieldValue(
                            'data.payloadGenerateRequest.validUntil',
                            (event && event.format('YYYY-MM-DD')) || ''
                          )
                        }
                        label='Valid Until *'
                        format='DD-MM-YYYY'
                      />
                    </LocalizationProvider>
                  </>
                )}
                <TextField
                  id='image'
                  label='Voucher Image *'
                  type='file'
                  placeholder='Image'
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    fileOnChange(e, 'image', (value) => setPreviewImage(value))
                  }
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {previewImage && (
                  <Grid item xs={12}>
                    <Typography {...DialogStyle.Title}>Voucher Image</Typography>
                    <Box {...DialogStyle.Image}>
                      <BackdropImage
                        open={previewBackdrop}
                        onOpen={() => setPreviewBackdrop(true)}
                        onClose={() => setPreviewBackdrop(false)}
                      >
                        <img
                          src={previewImage as string | undefined}
                          alt='Voucher Image'
                          width='100%'
                        />
                      </BackdropImage>
                    </Box>
                  </Grid>
                )}
                <TextField
                  id='banner'
                  label='Voucher Image Banner *'
                  type='file'
                  placeholder='Image Banner'
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    fileOnChange(e, 'banner', (value) => setPreviewBanner(value))
                  }
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {previewBanner && (
                  <Grid item xs={12}>
                    <Typography {...DialogStyle.Title}>Voucher Image Banner</Typography>
                    <Box {...DialogStyle.Image}>
                      <BackdropImage
                        open={bannerBackdrop}
                        onOpen={() => setBannerBackdrop(true)}
                        onClose={() => setBannerBackdrop(false)}
                      >
                        <img
                          src={previewBanner as string | undefined}
                          alt='Voucher Image Banner'
                          width='100%'
                        />
                      </BackdropImage>
                    </Box>
                  </Grid>
                )}
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='carModelList'
                  value={carModel}
                  onOpen={() => getCarModelDropdownList()}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.name == value.name : false
                  }
                  onChange={(event, newValue) => {
                    setCarModel(newValue)
                  }}
                  options={(carModelList && carModelList.data) || []}
                  getOptionLabel={(option) => option.name}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='Car Model' />}
                />
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='fixed-tags-demo'
                  value={userTypeProvince}
                  onOpen={() => getProvinceDropdown()}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.provinceName == value.provinceName : false
                  }
                  onChange={(event, newValue) => {
                    setUserTypeProvince(newValue);
                    const updatedLocations = newValue.map(province => ({
                      cityId: 0, // Default or selected city ID
                      cityName: '', // Default or selected city name
                      provinceId: province.provinceId,
                      provinceName: province.provinceName,
                    }));
                    formik.setFieldValue('data.locations', updatedLocations);
                  }}
                  options={(provinceDropdown && provinceDropdown.data) || []}
                  getOptionLabel={(option) => option.provinceName}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={option.provinceName} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='Province' />}
                />
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='fixed-tags-demo'
                  value={userTypeCity}
                  onOpen={() => {
                    const selectedProvinceIds = userTypeProvince.map(province => province.provinceId);
                    getCityDropdown(selectedProvinceIds);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.cityName == value.cityName : false
                  }
                  onChange={(event, newValue) => {
                    setUserTypeCity(newValue);
                    // Update locations with selected cities
                    const updatedLocations = newValue.map(city => ({
                      cityId: city.cityId,
                      cityName: city.cityName,
                      provinceId: userTypeProvince[0]?.provinceId || 0, // Assuming the first selected province
                      provinceName: userTypeProvince[0]?.provinceName || '', // Assuming the first selected province
                    }));
                    formik.setFieldValue('data.locations', updatedLocations);
                  }}
                  options={(cityDropdown && cityDropdown.data) || []}
                  getOptionLabel={(option) => `${option.cityName} - ${option.provinceName}`}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={`${option.cityName} - ${option.provinceName}`} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='City' />}
                  disabled={userTypeProvince.length === 0} // Disable if no province is selected
                />
                <Typography><strong>Vehicle Registration Date</strong></Typography>
                <div style={{ justifyContent: 'space-between', flexDirection: 'row', display: 'flex' }}>
                  {data?.registrationDateFrom === null || isEmpty(data?.registrationDateFrom) || data?.registrationDateFrom === '-' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={null}
                        onChange={(e: Dayjs | null) => {
                          const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                          formik.setFieldValue('data.registrationDateFrom', newDate)
                          setMinimum(newDate);
                        }}
                        label='Minimum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : null}
                        // onChange={(e: Dayjs | null) => {
                        //   const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                        //   formik.setFieldValue('data.registrationDateFrom', newDate)
                        //   setMinimum(newDate);
                        // }}
                        onChange={handleRegistrationDateFromChange}
                        label='Minimum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  )}
                  {data?.registrationDateTo === null || isEmpty(data?.registrationDateTo) || data?.registrationDateTo === '-' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={null}
                        minDate={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : undefined}
                        onChange={(e: Dayjs | null) => {
                          const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                          formik.setFieldValue('data.registrationDateTo', newDate)
                          setMaximum(newDate);
                        }}
                        onError={(error) => {
                          if (error) {
                            formik.setFieldError('data.registrationDateTo', error);
                            setMessageUntil(formik.values.data.registrationDateFrom)
                          } else if (formik.values.data.registrationDateFrom && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))) {
                            formik.setFieldError('data.registrationDateTo', 'Maximum Date must be greater than Minimum Date');
                          }
                        }}
                        label='Maximum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={formik.values.data && formik.values.data.registrationDateTo ? dayjs(formik.values.data.registrationDateTo) : null}
                        minDate={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : undefined}
                        // onChange={(e: Dayjs | null) => {
                        //   const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                        //   formik.setFieldValue('data.registrationDateTo', newDate)
                        //   setMaximum(newDate);
                        // }}
                        disabled={isMaxDateDisabled}
                        onChange={handleRegistrationDateToChange}
                        onError={(error) => {
                          if (error) {
                            formik.setFieldError('data.registrationDateTo', error);
                            setMessageUntil(formik.values.data.registrationDateFrom)
                          } else if (formik.values.data.registrationDateFrom && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))) {
                            formik.setFieldError('data.registrationDateTo', 'Maximum Date must be greater than Minimum Date');
                          }
                        }}
                        label='Maximum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%', marginLeft: 2 }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          formik.setFieldValue('data.registrationDateFrom', null); // Clear the date
                          setMinimum(''); // Clear the minimum state
                          formik.setFieldValue('data.registrationDateTo', null); // Clear the date
                          setMaximum(''); // Clear the maximum state
                        }}

                        disabled={isClearDisabled}
                        sx={{
                          padding: '4px',
                          minWidth: 'auto',
                          height: '26px',
                          width: '28px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginLeft: 1,
                          marginTop: 1.2,
                          backgroundColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                          borderColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                          color: isClearDisabled ? 'white' : 'white',
                          '&:hover': {
                            backgroundColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                            borderColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                            color: isClearDisabled ? 'white' : 'white',
                          },
                        }}
                      >
                        <Tooltip title="Clear" arrow>
                          <span style={{ fontSize: '1.3rem' }}>x</span>
                        </Tooltip>
                      </Button>
                    </LocalizationProvider>
                  )}
                </div>
                {messageUntil && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo)) && (
                  <FormHelperText sx={{ alignSelf: 'center', marginTop: -1.5 }} error id='registrationDateTo'>Maximum Date must be greater than Minimum Date</FormHelperText>
                )}
              </Box>
            </Box>
          )}
        </Container>
      </Dialog>
      <Notification
        open={!create.isLoading && !create.isUninitialized}
        onClose={() => (create.isError ? create.reset() : location.reload())}
        isError={Boolean(create.error) && create.isError}
        message={GLOBAL.returnExceptionMessage(create.isError, create.error as ErrorProps)}
      />
    </>
  )
}

export default Duplicate


export type PartialVoucherCreateProps = {
  image: File
  banner: File
  data: PartialVoucherCreateDataProps
  importToken: File
}

export type PartialVoucherCreateDataProps = {
  id?: number
  description: string
  nameEn: string
  nameId: string
  voucherType: string
  bookingType: string
  voucherCategoryId: number | null
  voucherSubCategoryId: number | null
  voucherTokenCategoryId: number | null
  partnerId: number | null
  applicableAreaMode: string | null
  informationEn: string
  informationId: string
  termsAndConditionsEn: string
  termsAndConditionsId: string
  generateType: string
  // approvalDate: string
  visibility?: string
  payloadGenerateRequest?: PartialVoucherCreateDataRequestProps
  carModels: CarModelDropDown[]
  locations: LocationDropDown[]
  registrationDateFrom: string
  registrationDateTo: string
}

export type PartialVoucherCreateDataRequestProps = {
  requestName: string
  quantity: string
  validFrom: string
  validUntil: string
}

export type LocationDropDown = {
  cityId: number
  cityName: string
  provinceId: number
  provinceName: string
}

export type CarModelDropDown = {
  id: number
  name: string
}